<template>
    <div class="video-generator">
        <!-- Generator Interface -->
        <div class="generator-interface max-md:!p-0" :class="{ 'with-inspiration': showInspirationRecommend }">
            <div
                class="flex w-full max-md:flex-col justify-between items-center"
            >
                <!-- Navigation Tabs -->
                <div class="generator-tabs max-md:w-full max-md:justify-center">
                    <div
                        v-for="(tab, index) in tabs"
                        :key="index"
                        class="tab-item max-md:!px-3 max-md:flex-1 hover:text-white hover:bg-white/5"
                        :class="{ active: activeTab === index }"
                        @click="setActiveTab(index)"
                    >
                        <span
                            :class="
                                logoEnums[tab?.value as keyof typeof logoEnums]
                            "
                            class="w-4 h-4"
                        ></span>

                        {{ formatLabel(tab.name) }}
                        <svg
                            v-if="tab.external"
                            class="w-4 h-4 ml-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                </div>
                <div
                    class="model-selector-wrapper md:max-w-[400px] max-md:w-full max-md:mt-5"
                >
                    <tool-new-model-select
                        v-model:model="selectedModelKey"
                        :model_list="currentModelList"
                        :type="currentModelType"
                        :style_class="model_select_style"
                        :hideLabel="true"
                        popper_style="popper_style"
                    ></tool-new-model-select>
                </div>
            </div>

            <!-- Header Section with Title and Model Selector -->

            <!-- Tab Content -->
            <div>
                <!-- Image to Video Tab -->
                <div v-if="activeTab === 0" class="tab-panel">
                    <!-- 统一容器 -->
                    <div class="unified-container" :class="{ 'with-inspiration': showInspirationRecommend }">
                        <!-- 左侧上传区域 -->
                        <div class="upload-area">
                            <div class="input-section relative">
                                <NewModelPageImageUpload
                                    ref="imageUploadRef"
                                    :max-size-m-b="10"
                                    :max-resolution="4096"
                                    :allow-multiple="false"
                                    accept-types="image/jpeg,image/png,image/webp"
                                    @upload-success="onImageUploadSuccess"
                                    @upload-error="onImageUploadError"
                                    @files-change="onImageFilesChange"
                                />

                                <!-- Generate Button -->
                                <div class="text-center absolute bottom-5 w-full">
                                    <button
                                        v-if="isLoggedIn"
                                        class="generate-button shadow-[0_0_20px_rgba(153,247,104,0.4)] animate-cta-pulse transition-all duration-300"
                                        :class="{
                                            disabled: !canGenerate,
                                            loading: isGenerating,
                                        }"
                                        :disabled="!canGenerate || isGenerating"
                                        @click.stop="handleGenerate($event)"
                                    >
                                        <div class="btn-icon">
                                            <img
                                                src="~/assets/images/common/create_btn.svg"
                                                alt="Create icon"
                                                aria-hidden="true"
                                                role="presentation"
                                                class="w-6 h-6"
                                            />
                                        </div>
                                        <span class="btn-text">
                                            {{
                                                isGenerating
                                                    ? t('menus.creating')
                                                    : t('menus.create')
                                            }}
                                        </span>
                                    </button>
                                <div
                                    v-else-if="!isLoggedIn && LOGIN_ENTRY_ENABLED"
                                        class="min-w-[253px] h-[52px] px-3 rounded-[10px] bg-[#99F768] justify-center inline-flex items-center cursor-pointer hover:bg-[#82d945] hover:scale-105 shadow-[0_0_20px_rgba(153,247,104,0.4)] animate-cta-pulse transition-all duration-300"
                                        @click.stop="handleTryNow"
                                    >
                                        <img
                                            class="w-5 h-5"
                                            src="https://static.weryai.com/growth/image/weryai_ce29b49010526e7fa8e91d971a726de4.webp"
                                            alt="Gift icon"
                                            aria-hidden="true"
                                            role="presentation"
                                        />
                                        <span
                                            class="text-base text-[#080808] font-medium ml-[5px]"
                                            >{{ t('common.loginToGetFree') }}</span
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 右侧灵感推荐 -->
                        <div v-if="showInspirationRecommend" class="inspiration-area">
                            <InspirationRecommend 
                                :model-key="props.modelKey" 
                                :filter-type="currentTabType"
                            />
                        </div>
                    </div>
                    
                    <p class="upload-hint text-center !mt-[10px] upload-detail">
                        {{ t('upload.supportedImageFormats') }}
                        {{
                            t('upload.imageConstraints', {
                                maxSize: 10,
                                maxResolution: 4096,
                            })
                        }}
                    </p>
                </div>

                <!-- Text to Video Tab -->
                <div v-if="activeTab === 1" class="tab-panel">
                    <!-- 统一容器 -->
                    <div class="unified-container" :class="{ 'with-inspiration': showInspirationRecommend }">
                        <!-- 左侧文本输入区域 -->
                        <div class="text-area">
                            <textarea
                                v-model="promptText"
                                class="prompt-textarea"
                                :placeholder="t('menus.promptPlaceholder')"
                            ></textarea>
                            
                            <!-- 底部按钮区域 -->
                            <div class="button-area">
                                <button
                                    v-if="isLoggedIn"
                                    class="generate-button shadow-[0_0_20px_rgba(153,247,104,0.4)] animate-cta-pulse transition-all duration-300"
                                    :class="{
                                        disabled: !canGenerate,
                                        loading: isGenerating,
                                    }"
                                    :disabled="!canGenerate || isGenerating"
                                    @click.stop="handleGenerate($event)"
                                >
                                    <div class="btn-icon">
                                        <img
                                            src="~/assets/images/common/create_btn.svg"
                                            alt="Create icon"
                                            aria-hidden="true"
                                            role="presentation"
                                            class="w-6 h-6"
                                        />
                                    </div>
                                    <span class="btn-text">
                                        {{
                                            isGenerating
                                                ? t('menus.creating')
                                                : t('menus.create')
                                        }}
                                    </span>
                                </button>
                                <div
                                    v-else-if="!isLoggedIn && LOGIN_ENTRY_ENABLED"
                                    class="min-w-[253px] h-[52px] rounded-[10px] bg-[#99F768] justify-center inline-flex items-center cursor-pointer hover:bg-[#82d945] hover:scale-105 shadow-[0_0_20px_rgba(153,247,104,0.4)] animate-cta-pulse transition-all duration-300"
                                    @click="handleTryNow"
                                >
                                    <img
                                        class="w-5 h-5"
                                        src="https://static.weryai.com/growth/image/weryai_ce29b49010526e7fa8e91d971a726de4.webp"
                                        alt="Gift icon"
                                        aria-hidden="true"
                                        role="presentation"
                                    />
                                    <span
                                        class="text-base text-[#080808] font-medium ml-[5px]"
                                        >{{ t('common.loginToGetFree') }}</span
                                    >
                                </div>
                            </div>
                        </div>

                        <!-- 右侧灵感推荐 -->
                        <div v-if="showInspirationRecommend" class="inspiration-area">
                            <InspirationRecommend 
                                :model-key="props.modelKey" 
                                :filter-type="currentTabType"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { navigateTo } from '#app'
import ModelPageImageUpload from '~/components/ModelPageImageUpload.vue'
import ToolModelSelect from '~/components/Tool/ModelSelect.vue'
import InspirationRecommend from '~/components/InspirationRecommend.vue'
import { useI18n } from '~/composables/useI18n'
import { LOGIN_ENTRY_ENABLED } from '~/utils/feature-flags'
import { analyticsEvent } from '~/utils/log-event-func'
import { useToolsHooks } from '@/utils/useHooks'
import { useAppStore } from '~/stores/app'
import { formatAndGroupData } from '~/utils/common'
import { showLoginModal } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const { t } = useI18n()
const { handleMenuLinkClick } = useToolsHooks()
const appStore = useAppStore()
const modelData = computed(() => appStore.modelData)
// 类型定义
interface UploadedFile {
    id: string
    file: File
    name: string
    size: number
    previewUrl: string
    uploadStatus: 'pending' | 'uploading' | 'success' | 'error'
    uploadResult?: unknown
    errorMessage?: string
}

interface UploadResult {
    data?: {
        url?: string
        object_url_list?: string[]
    }
    url?: string
}

// Props
interface Props {
    modelName?: string
    modelVersion?: string
    tabs?: Array<{ name: string; external?: boolean; value?: string }>
    redirectUrl?: string
    modelKey?: string
}

const props = withDefaults(defineProps<Props>(), {
    modelName: 'Veo 3.1',
    modelVersion: '',
    tabs: () => [
        { name: '图生视频', external: false },
        { name: '文生视频', external: false },
    ],
    redirectUrl: '/ai-video',
    modelKey: 'google-veo3-1',
})

const logoEnums = {
    image: 'i-edit-pic-icon',
    text: 'i-pen-icon',
}

const formatLabel = (name: string) => {
    if (!name) return ''
    // 根据中文名称返回对应的翻译
    if (name.includes('图生视频')) return t('menus.createModal.imageToVideo')
    if (name.includes('文生视频')) return t('menus.createModal.textToVideo')
    if (name.includes('视频编辑')) return t('menus.createModal.videoEditor')

    return name
}

// 当前模型类型
const currentModelType = computed(() => {
    // activeTab: 0 = 图生视频, 1 = 文生视频
    return activeTab.value === 0 ? 'image_to_video' : 'text_to_video'
})

// 当前模型列表（只显示父模型，不显示版本）
const currentModelList = computed(() => {
    if (!modelData.value) return []
    const videoModels =
        modelData.value?.video_models?.[currentModelType.value] || []
    // 使用 formatAndGroupData 过滤掉子模型，只保留父模型
    const finalData = formatAndGroupData(videoModels)
    return finalData
})

// 选中的模型key
const selectedModelKey = ref('')

// 当前选中的模型对象
const currentSelectedModel = computed(() => {
    if (!selectedModelKey.value || !currentModelList.value) return null
    return (
        currentModelList.value.find(
            model => model.model_key === selectedModelKey.value
        ) || null
    )
})

// Emits
const emit = defineEmits<{
    generate: [data: { type: 'image' | 'text'; image?: File; prompt?: string }]
    'tab-change': [tabType: string]  // 添加标签页切换事件
}>()

// Reactive data
const activeTab = ref(0)
const uploadedImage = ref<File | null>(null)
const uploadedImageInfo = ref<UploadedFile | null>(null)
const promptText = ref('')

// 当前标签页对应的类型
const currentTabType = computed(() => {
    return activeTab.value === 0 ? 'IMG_TO_VIDEO' : 'TEXT_TO_VIDEO'
})

// 初始化时通知父组件当前标签页类型
onMounted(() => {
    emit('tab-change', currentTabType.value)
})

const isGenerating = ref(false)
const imageUploadRef = ref<any>(null)

// 判断是否显示灵感推荐
// 只有 Veo3.1、Veo3、Sora2、Runway 这些模型才显示灵感推荐
const showInspirationRecommend = computed(() => {
    const supportedModels = ['sora', 'google-veo3-1', 'google-veo3', 'RUNWAY']
    return supportedModels.includes(props.modelKey)
})

// Computed
const canGenerate = computed(() => {
    if (activeTab.value === 0) {
        return uploadedImage.value !== null
    } else {
        return promptText.value.trim().length > 0
    }
})

// Methods
const setActiveTab = (index: number) => {
    activeTab.value = index
    // 通知父组件标签页变化
    const type = index === 0 ? 'IMG_TO_VIDEO' : 'TEXT_TO_VIDEO'
    emit('tab-change', type)
}

const getCurrentTabTitle = () => {
    return formatLabel(props.tabs[activeTab.value]?.name || '')
}

// Watch模型列表变化，根据 modelName prop 或自动选择第一个模型
watch(
    [() => currentModelList.value, () => activeTab.value],
    ([newModelList]: [any[], number]) => {
        if (newModelList && newModelList.length > 0) {
            // 如果还没有选中模型，或者当前选中的模型不在列表中
            if (
                !selectedModelKey.value ||
                !newModelList.find(
                    (m: any) => m.model_key === selectedModelKey.value
                )
            ) {
                let matchedModel = null

                // 优先使用 modelKey prop 进行精确匹配
                if (props.modelKey) {
                    matchedModel = newModelList.find(
                        (m: any) => m.model_key === props.modelKey
                    )
                }

                // 如果 modelKey 没有匹配，尝试根据 modelName prop 找到对应的模型
                if (!matchedModel && props.modelName) {
                    const modelNameLower = props.modelName.toLowerCase().trim()

                    // 第一步：尝试精确匹配（title 或 model_key）
                    matchedModel = newModelList.find((m: any) => {
                        const titleLower = (m.title || '').toLowerCase().trim()
                        const keyLower = (m.model_key || '')
                            .toLowerCase()
                            .trim()
                        console.log('精准匹配中---')
                        return (
                            titleLower === modelNameLower ||
                            keyLower === modelNameLower
                        )
                    })

                    // 第二步：如果精确匹配失败，尝试模糊匹配
                    if (!matchedModel) {
                        matchedModel = newModelList.find((m: any) => {
                            const titleLower = (m.title || '')
                                .toLowerCase()
                                .trim()
                            const keyLower = (m.model_key || '')
                                .toLowerCase()
                                .trim()

                            // 使用单词边界匹配，避免 "Veo 3" 匹配到 "Veo 3.1"
                            // 检查 modelName 是否是 title 或 key 的开头部分
                            return (
                                titleLower.startsWith(modelNameLower) ||
                                keyLower.startsWith(modelNameLower) ||
                                titleLower.includes(' ' + modelNameLower) ||
                                modelNameLower.includes(titleLower)
                            )
                        })
                    }
                }

                // 如果找到匹配的模型，使用它；否则使用第一个模型
                selectedModelKey.value =
                    matchedModel?.model_key || newModelList[0]?.model_key || ''
            }
        }
    },
    {
        immediate: true,
        flush: 'post',
    }
)

const is_client = import.meta.client
const handleGenerate = (event: MouseEvent) => {
    if (!canGenerate.value) return

    // 文生视频 Tab：保存提示词并跳转
    if (activeTab.value === 1) {
        const prompt = promptText.value.trim()
        if (prompt && is_client) {
            // 保存提示词和选中的模型到 localStorage
            localStorage.setItem('modelPagePrompt', prompt)
            // 保存选中的模型 key，如果为空则保存空字符串
            localStorage.setItem(
                'modelPageModelKey',
                selectedModelKey.value || ''
            )
            // 保存模型版本，如果有的话
            if (props.modelVersion) {
                localStorage.setItem(
                    'modelPageModelVersion',
                    props.modelVersion
                )
                console.log(
                    '✅ [VideoGenerator] 保存模型版本到 localStorage:',
                    props.modelVersion
                )
            }

            // 埋点：根据模型名称上报不同的埋点事件
            if (
                props.modelName === 'Google Veo 3.1' ||
                props.modelName === 'Veo 3.1'
            ) {
                analyticsEvent('click_veo31_next', {
                    tab_type: 'text-to-video',
                })
            } else if (props.modelName === 'Hailuo 2.3') {
                analyticsEvent('click_hailuo23_next', {
                    tab_type: 'text-to-video',
                })
            } else if (props.modelName === 'Seedance 1.0 Pro Fast') {
                analyticsEvent('click_seedance_pro_fast_next', {
                    tab_type: 'text-to-video',
                })
            } else {
                analyticsEvent('click_sora2_next', {
                    tab_type: 'text-to-video',
                })
            }
            // 跳转到文生视频页面，使用正确的路径格式
            const selectedModel = currentSelectedModel.value
            if (
                selectedModel &&
                selectedModel.supportType &&
                selectedModel.model_key
            ) {
                const encodedPrompt = encodeURIComponent(prompt)
                const selectedModelParam = selectedModelKey.value
                    ? `&selectedModel=${selectedModelKey.value}`
                    : ''
                const path = `/${selectedModel.supportType[0]}?defaultModel=${selectedModel.model_key}${selectedModel.version ? '&version=' + selectedModel.version : ''}&customType=modelPage&prompt=${encodedPrompt}${selectedModelParam}`
                handleClickModelPageCommonCreate(
                    selectedModelKey.value || selectedModelKey.value || '',
                    selectedModel?.version || ''
                )
                navigateTo(path)
            } else {
                // fallback to default path
                const encodedPrompt = encodeURIComponent(prompt)
                const selectedModelParam = selectedModelKey.value
                    ? `&selectedModel=${selectedModelKey.value}`
                    : ''
                // handleMenuLinkClick('/text-to-video?customType=modelPage', event)
                handleClickModelPageCommonCreate(
                    selectedModelKey.value || selectedModelKey.value || '',
                    selectedModel?.version || ''
                )
                handleMenuLinkClick(
                    `/text-to-video?defaultModel=${props.modelKey}${props.modelVersion ? '&version=' + props.modelVersion : ''}&customType=modelPage&prompt=${encodedPrompt}${selectedModelParam}`,
                    event
                )
            }
        }
    }

    // 图生视频 Tab：图片上传成功后会自动跳转，这里不需要处理
}

// ModelPageImageUpload 事件处理
const onImageUploadSuccess = (files: UploadedFile[]) => {
    if (files.length > 0 && files[0]) {
        uploadedImage.value = files[0].file
        uploadedImageInfo.value = files[0]

        // 获取上传后的图片地址
        const uploadResult = files[0].uploadResult as UploadResult
        const imageUrl =
            uploadResult?.data?.object_url_list?.[0] ||
            uploadResult?.data?.url ||
            uploadResult?.url

        if (imageUrl && is_client) {
            // 写入 localStorage
            localStorage.setItem('modelPageFileUrl', imageUrl)
            // 保存选中的模型 key，如果为空则保存空字符串
            localStorage.setItem(
                'modelPageModelKey',
                selectedModelKey.value || ''
            )
            // 保存模型版本，如果有的话
            if (props.modelVersion) {
                localStorage.setItem(
                    'modelPageModelVersion',
                    props.modelVersion
                )
                console.log(
                    '✅ [VideoGenerator] 保存模型版本到 localStorage:',
                    props.modelVersion
                )
            }

            // 埋点：根据模型名称上报不同的埋点事件
            if (
                props.modelName === 'Google Veo 3.1' ||
                props.modelName === 'Veo 3.1'
            ) {
                analyticsEvent('click_veo31_next', {
                    tab_type: 'image-to-video',
                })
            } else if (props.modelName === 'Hailuo 2.3') {
                analyticsEvent('click_hailuo23_next', {
                    tab_type: 'image-to-video',
                })
            } else if (props.modelName === 'Seedance 1.0 Pro Fast') {
                analyticsEvent('click_seedance_pro_fast_next', {
                    tab_type: 'image-to-video',
                })
            } else {
                analyticsEvent('click_sora2_next', {
                    tab_type: 'image-to-video',
                })
            }

            // 跳转到图生视频页面，使用正确的路径格式
            const selectedModel = currentSelectedModel.value
            if (
                selectedModel &&
                selectedModel.supportType &&
                selectedModel.model_key
            ) {
                const encodedImageUrl = encodeURIComponent(imageUrl)
                const selectedModelParam = selectedModelKey.value
                    ? `&selectedModel=${selectedModelKey.value}`
                    : ''
                const path = `/${selectedModel.supportType[0]}?defaultModel=${selectedModel.model_key}${selectedModel.version ? '&version=' + selectedModel.version : ''}&customType=modelPage&imageUrl=${encodedImageUrl}${selectedModelParam}`
                handleClickModelPageCommonCreate(
                    selectedModelKey.value || selectedModelKey.value || '',
                    selectedModel?.version || ''
                )
                navigateTo(path)
            } else {
                // fallback to default path
                const encodedImageUrl = encodeURIComponent(imageUrl)
                const selectedModelParam = selectedModelKey.value
                    ? `&selectedModel=${selectedModelKey.value}`
                    : ''

                handleClickModelPageCommonCreate(
                    selectedModelKey.value || props?.modelKey,
                    props?.modelVersion || ''
                )
                // handleMenuLinkClick('/image-to-video?customType=modelPage')
                handleMenuLinkClick(
                    `/image-to-video?defaultModel=${props.modelKey}${props.modelVersion ? '&version=' + props.modelVersion : ''}&customType=modelPage&imageUrl=${encodedImageUrl}${selectedModelParam}`
                )
            }
        }
    }
}

const handleClickModelPageCommonCreate = (
    model_name?: string,
    model_version?: string
) => {
    analyticsEvent('click_model_page_common_create', {
        model_name: model_name || '',
        model_version: model_version || '',
    })
}

const onImageUploadError = (error: string) => {
    console.error('Image upload failed:', error)
}

const onImageFilesChange = (files: UploadedFile[]) => {
    if (files.length === 0) {
        uploadedImage.value = null
        uploadedImageInfo.value = null
    } else if (files[0]) {
        uploadedImage.value = files[0].file
    }
}

const handleTryNow = () => {
    showLoginModal()
}

// 初始化时获取模型数据
onMounted(() => {
    if (!modelData.value) {
        appStore.getModelList()
    }
})

const model_select_style = `border-radius: 9999px;
border: 1px solid rgba(255, 255, 255, 0.10);
background: rgba(0, 0, 0, 0.20);
backdrop-filter: blur(6px);`
</script>

<style lang="less" scoped>
.video-generator {
    width: 100%;
    max-width: 1300px;
    margin: 0 auto;
}

.generator-interface {
    &.with-inspiration {
        max-width: 1000px;
    }

    &:not(.with-inspiration) {
        max-width: 900px;
        margin: 0 auto;
    }
}

// 统一容器 - 所有内容在一个盒子里
.unified-container {
    &.with-inspiration {
        border-radius: 24px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(50px);
        overflow: hidden;
        display: flex;
        align-items: stretch;
    }
    
    &:not(.with-inspiration) {
        // 没有灵感推荐时，不使用统一容器样式，保持原样
        .upload-area,
        .text-area {
            width: 100%;
        }
    }
}

// 左侧上传/文本区域
.upload-area,
.text-area {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.upload-area {
    // 当在统一容器内时，充满整个区域且无圆角
    .unified-container.with-inspiration & {
        padding: 0;
        height: 100%;
        min-height: 314px;
        display: flex;
        flex-direction: column;
        
        .input-section {
            height: 100%;
            min-height: auto;
            flex: 1;
            display: flex;
            flex-direction: column;
        }
        
        // 移除上传组件的圆角、边框、背景等样式，让它充满整个左侧区域
        :deep(.model-page-image-upload) {
            height: 100%;
            display: flex;
            flex-direction: column;
            
            .upload-area {
                border-radius: 0 !important;
                border: none !important;
                background: transparent !important;
                backdrop-filter: none !important;
                height: 100% !important;
                min-height: 314px !important;
                padding: 8rem 2rem !important;
                flex: 1;
                
                // 移除悬浮效果
                &:hover {
                    background: transparent !important;
                    box-shadow: none !important;
                    border: none !important;
                    transform: none !important;
                }
                
                &.has-files {
                    background: transparent !important;
                }
                
                // 拖拽时只显示轻微的提示，不影响布局
                &.drag-over {
                    background: rgba(255, 255, 255, 0.03) !important;
                    border: none !important;
                    transform: none !important;
                }
            }
        }
    }
}

.text-area {
    display: flex;
    flex-direction: column;
    gap: 20px;
    
    // 有灵感推荐时的样式
    .unified-container.with-inspiration & {
        padding: 20px 24px;
        height: 100%;
        min-height: 314px;
    }
    
    // 没有灵感推荐时，保持原来的样式
    .unified-container:not(.with-inspiration) & {
        width: 100%;
        min-height: 200px;
        border-radius: 24px;
        border: 1px solid rgba(255, 255, 255, 0.08);
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(25px);
        text-align: center;
        padding: 10px 10px 24px;
    }
}

// 右侧灵感推荐区域
.inspiration-area {
    width: 303px;
    flex-shrink: 0;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    
    // 移除灵感推荐组件自己的容器样式，因为现在在统一容器内
    :deep(.inspiration-recommend) {
        border: none;
        border-radius: 0;
        background: transparent;
        backdrop-filter: none;
    }
}

.button-area {
    display: flex;
    justify-content: center;
    align-items: center;
    
    // 没有灵感推荐时的样式
    .unified-container:not(.with-inspiration) & {
        margin-top: 1.25rem;
    }
}

@media (max-width: 1024px) {
    .unified-container.with-inspiration {
        flex-direction: column;
    }

    .inspiration-area {
        width: 100%;
        border-left: none;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
}

.generator-btn-icon {
    width: 1.25rem;
    height: 1.25rem;
}

.generator-interface {
    padding: 2rem;
    border-radius: 1.5rem;
}

.generator-tabs {
    display: inline-flex;
    border-radius: 9999px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -4px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(12px);
    gap: 5px;
    padding: 4px;
}

.tab-item {
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 9999px;
    color: #fff;
    font-family: 'Noto Sans SC';
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    border: 1px solid transparent;
    &:hover {
        color: #99f768;
    }

    &.active {
        color: #99f768;
        border-radius: 9999px;
        border-color: rgba(153, 247, 104, 0.3);
        background: rgba(255, 255, 255, 0.05);
        box-shadow: 0 0 15px 0 rgba(153, 247, 104, 0.1);
    }
}

.tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    padding: 1rem 1.5rem 0;
}

.section-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    margin: 0;
}

.model-selector-wrapper {
    flex: 1;
}

.tab-panel {
    animation: fadeIn 0.3s ease-in-out;
    background: transparent;
    border-radius: 0;
    padding: 1.25rem 0 0;
    border: none;
    backdrop-filter: none;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.input-section {
    position: relative;
    
    // 没有灵感推荐时的默认样式
    .unified-container:not(.with-inspiration) & {
        min-height: 314px;
    }
    
    // 有灵感推荐时充满容器
    .unified-container.with-inspiration .upload-area & {
        height: 100%;
        min-height: 314px;
        display: flex;
        flex-direction: column;
    }
}

.input-label {
    display: block;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 0.5rem;
}

.prompt-textarea {
    width: 100%;
    color: white;
    resize: none;
    background: transparent;
    border: none;
    
    &::placeholder {
        color: rgba(255, 255, 255, 0.6);
    }

    &:focus {
        --tw-ring-color: transparent;
        outline: none;
    }
    
    // 有灵感推荐时的样式
    .unified-container.with-inspiration & {
        font-size: 14px;
        font-weight: 300;
        line-height: 26px;
        flex: 1;
        min-height: 180px;
        padding: 0;
        
        &::placeholder {
            font-size: 14px;
            font-weight: 300;
            line-height: 26px;
        }
    }
    
    // 没有灵感推荐时的样式（原来的样式）
    .unified-container:not(.with-inspiration) & {
        font-size: 16px;
        min-height: 100px;
        padding-bottom: 1.5rem;
        border-radius: 24px;
        
        &::placeholder {
            font-size: 16px;
        }
    }
}

.generate-section {
    text-align: center;
    padding: 1rem 1.5rem 1.5rem;
    border-top: none;
    margin-top: 1rem;
}

.generate-button {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 2.5rem;
    color: black;
    border: none;
    border-radius: 0.75rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    opacity: 1;
    max-width: 300px;
    margin: 0 auto;
    width: 100%;
    justify-content: center;
    background-color: var(--weryai-text-active);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
        );
        transition: left 0.5s ease;
    }

    &:hover:not(.disabled) {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(153, 247, 104, 0.5);

        &::before {
            left: 100%;
        }

        .btn-sparkles {
            transform: translateX(4px);
        }
    }

    &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        border: 1px solid rgba(255, 255, 255, 0.08);
        backdrop-filter: blur(25px);
        &:hover {
            transform: none;
            box-shadow: none;
        }
    }

    &.loading {
        cursor: not-allowed;
        color: white;
        opacity: 1;
    }

    &:active {
        transform: translateY(0);
    }
}

.btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
}

.btn-text {
    font-weight: 600;
    letter-spacing: -0.01em;
}

.btn-sparkles {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    transition: transform 0.3s ease;
}

/* Responsive Design */
@media (max-width: 768px) {
    .model-selector {
        justify-content: center;
    }
}

.upload-detail {
    font-size: 0.75rem;
    font-weight: 400;
    margin-top: 5px;
    color: rgb(255 255 255 / 0.6);
}
.upload-hint {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0;
}
</style>

<style>
.popper_style {
    background: rgba(0, 0, 0, 0.2) !important;
    backdrop-filter: blur(6px) !important;
}
</style>
